import React, { useState } from 'react';
import styled from 'styled-components';
import {
  FaCog,
  FaUser,
  FaUsers,
  FaLock,
  FaBell,
  FaDatabase,
  FaPlug,
  FaSave,
  FaCheck,
  FaExclamationTriangle,
  FaShareAlt,
  FaChartBar,
  FaUserSecret,
} from 'react-icons/fa';
import SocialPanel from './SocialPanel';
import DummyAccountsPanel from './DummyAccountsPanel';
import ElectionsTab from './tabs/ElectionsTab';
import UsersPanel from './UsersPanel';
import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechInput,
  TechSelect,
  TechTextarea,
  TechButton,
  TechButtonGhost,
  TechChip,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';

/* ---------- styled específicos ---------- */

const Layout = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.25rem;

  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Sidebar = styled.aside`
  position: relative;
  background: var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  padding: 0.6rem;
  height: fit-content;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const NavBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  text-align: left;
  background: ${({ active }) => (active ? 'rgba(0, 180, 216, 0.1)' : 'transparent')};
  color: ${({ active }) => (active ? 'var(--text-primary)' : 'var(--text-secondary)')};
  border: 1px solid ${({ active }) => (active ? 'rgba(0, 180, 216, 0.3)' : 'transparent')};
  border-radius: 6px;
  padding: 0.55rem 0.75rem;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.86rem;
  margin-bottom: 4px;
  transition: all 0.18s ease;

  svg {
    color: ${({ active }) => (active ? accent : 'var(--text-disabled)')};
    font-size: 0.85rem;
  }

  &:hover {
    color: var(--text-primary);
    background: rgba(255, 255, 255, 0.03);
  }
`;

const Field = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-bottom: 0.4rem;
  }

  .help {
    margin-top: 0.4rem;
    font-size: 0.78rem;
    color: var(--text-disabled);
  }
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 0.7rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 5px;
  cursor: pointer;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: var(--text-secondary);
  transition: border-color 0.18s ease;

  &:hover { border-color: rgba(0, 180, 216, 0.3); }

  input { accent-color: ${accent}; }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const RoleCard = styled.div`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${accent};
  border-radius: 5px;
  padding: 0.7rem 0.85rem;
  margin-bottom: 0.5rem;

  h5 { margin: 0 0 0.3rem; color: var(--text-primary); font-size: 0.92rem; }
  p { margin: 0 0 0.35rem; color: var(--text-secondary); font-size: 0.82rem; line-height: 1.4; }
`;

const IntegrationRow = styled.article`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  padding: 0.85rem 1rem;
  margin-bottom: 0.7rem;

  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.55rem;

  h5 { margin: 0; color: var(--text-primary); font-size: 0.95rem; }
  p { margin: 0.3rem 0; color: var(--text-secondary); font-size: 0.82rem; line-height: 1.4; }
  .meta { font-family: ${mono}; font-size: 0.65rem; letter-spacing: 0.14em; text-transform: uppercase; color: var(--text-disabled); }
`;

/* ---------- data ---------- */

const NAV = [
  { id: 'general', label: 'General', icon: FaCog },
  { id: 'profile', label: 'Perfil', icon: FaUser },
  { id: 'users', label: 'Usuarios', icon: FaUsers },
  { id: 'security', label: 'Seguridad', icon: FaLock },
  { id: 'notifications', label: 'Notificaciones', icon: FaBell },
  { id: 'data', label: 'Datos', icon: FaDatabase },
  { id: 'integrations', label: 'Integraciones', icon: FaPlug },
  { id: 'social', label: 'Redes sociales', icon: FaShareAlt },
  { id: 'dummy-accounts', label: 'Cuentas dummy', icon: FaUserSecret },
  { id: 'elections', label: 'Elecciones', icon: FaChartBar },
];

// Integraciones reales que tiene la plataforma hoy.
// Se completan con su estado a partir del entorno (no son mock).
const apiIntegrations = [
  {
    name: 'Neon Postgres',
    status: 'success',
    lastSync: 'conexión persistente',
    description: 'Base de datos principal · users, competitors, competitor_news, research_items, research_insights, scrape_runs.',
  },
  {
    name: 'DeepSeek-Chat',
    status: 'success',
    lastSync: 'on-demand',
    description: 'LLM utilizado para el asistente IA, generación semanal de research e insights.',
  },
  {
    name: 'Google News RSS',
    status: 'success',
    lastSync: 'cron lunes 06:00 UTC',
    description: 'Feed de noticias scrapeadas para cada competidor con filtros AND-de-OR por nombre + ciudad.',
  },
  {
    name: 'Vercel Cron Jobs',
    status: 'success',
    lastSync: '0 6 * * 1 / 0 6 * * 0',
    description: 'Disparo automático de refresh-competitors (lunes) y weekly-research (domingo).',
  },
  {
    name: 'Meta Graph / X / TikTok APIs',
    status: 'warning',
    lastSync: 'no conectado',
    description: 'Integración pendiente · permitiría seguir followers, engagement y posts oficiales del candidato.',
  },
  {
    name: 'Encuesta interna · panel',
    status: 'warning',
    lastSync: 'no conectado',
    description: 'Pendiente · permitiría ingestar resultados de encuestas presenciales/telefónicas para alimentar KPIs de aprobación.',
  },
];

const userRoles = [
  { name: 'Administrador', description: 'Acceso completo a todas las funciones y configuraciones.', permissions: ['Lectura', 'Escritura', 'Eliminación', 'Configuración'] },
];

const statusTone = (s) => (s === 'success' ? 'success' : s === 'warning' ? 'warn' : s === 'danger' ? 'danger' : undefined);
const statusLabel = (s) => (s === 'success' ? 'Conectado' : s === 'warning' ? 'Atención' : 'Error');

/* ---------- componente ---------- */

const Configuration = () => {
  const [activeSection, setActiveSection] = useState('general');

  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return (
          <>
            <Field>
              <label>Nombre de la campaña</label>
              <TechInput defaultValue="Campaña Federico Chouhy 2025" />
            </Field>
            <Field>
              <label>Zona horaria</label>
              <TechSelect>
                <option>(GMT-03:00) Buenos Aires</option>
                <option>(GMT-05:00) Ciudad de México</option>
                <option>(GMT-05:00) Bogotá</option>
                <option>(GMT-04:00) Santiago</option>
                <option>(GMT-03:00) São Paulo</option>
              </TechSelect>
            </Field>
            <Field>
              <label>Idioma principal</label>
              <TechSelect>
                <option>Español</option>
                <option>Inglés</option>
                <option>Portugués</option>
              </TechSelect>
            </Field>
            <Field>
              <label>Descripción de la campaña</label>
              <TechTextarea defaultValue="Campaña a Concejal por La Libertad Avanza · San Nicolás de los Arroyos. Foco en gestión, transparencia y propuestas concretas." />
            </Field>
            <Actions>
              <TechButtonGhost type="button">Cancelar</TechButtonGhost>
              <TechButton type="button"><FaSave /> Guardar cambios</TechButton>
            </Actions>
          </>
        );

      case 'profile':
        return (
          <>
            <Field>
              <label>Nombre</label>
              <TechInput defaultValue="Federico Chouhy" />
            </Field>
            <Field>
              <label>Email</label>
              <TechInput type="email" defaultValue="federico@arcana.dev" />
            </Field>
            <Field>
              <label>Rol</label>
              <TechSelect>
                <option>Administrador</option>
                <option>Analista</option>
                <option>Estratega</option>
                <option>Comunicador</option>
                <option>Visualizador</option>
              </TechSelect>
            </Field>
            <Field>
              <label>Contraseña actual</label>
              <TechInput type="password" placeholder="••••••••" />
              <p className="help">Dejar en blanco si no querés cambiarla.</p>
            </Field>
            <Field>
              <label>Nueva contraseña</label>
              <TechInput type="password" placeholder="••••••••" />
            </Field>
            <Actions>
              <TechButtonGhost type="button">Cancelar</TechButtonGhost>
              <TechButton type="button"><FaSave /> Actualizar perfil</TechButton>
            </Actions>
          </>
        );

      case 'users':
        return <UsersPanel />;

      case 'security':
        return (
          <>
            <Field>
              <label>Autenticación de dos factores</label>
              <CheckRow>
                <input type="checkbox" defaultChecked />
                Habilitar 2FA para mi cuenta
              </CheckRow>
              <p className="help">Recomendado para mayor seguridad.</p>
            </Field>
            <Field>
              <label>Política de contraseñas</label>
              <TechSelect>
                <option>Alta · mínimo 12 caracteres + símbolos</option>
                <option>Media · mínimo 8 caracteres + números</option>
                <option>Básica · mínimo 6 caracteres</option>
              </TechSelect>
            </Field>
            <Field>
              <label>Tiempo de expiración de sesión</label>
              <TechSelect>
                <option>30 minutos</option>
                <option>1 hora</option>
                <option>4 horas</option>
                <option>8 horas</option>
              </TechSelect>
            </Field>
            <Field>
              <label>Auditoría</label>
              <CheckRow>
                <input type="checkbox" defaultChecked />
                Habilitar registro detallado de actividad
              </CheckRow>
            </Field>
            <Actions>
              <TechButtonGhost type="button">Cancelar</TechButtonGhost>
              <TechButton type="button"><FaSave /> Guardar configuración</TechButton>
            </Actions>
          </>
        );

      case 'notifications':
        return (
          <>
            <Field>
              <label>Notificaciones por email</label>
              <CheckRow><input type="checkbox" defaultChecked /> Alertas de reputación</CheckRow>
              <CheckRow><input type="checkbox" defaultChecked /> Informes diarios</CheckRow>
              <CheckRow><input type="checkbox" defaultChecked /> Menciones importantes</CheckRow>
            </Field>
            <Field>
              <label>Notificaciones en plataforma</label>
              <CheckRow><input type="checkbox" defaultChecked /> Alertas de reputación</CheckRow>
              <CheckRow><input type="checkbox" defaultChecked /> Asignación de tareas</CheckRow>
              <CheckRow><input type="checkbox" defaultChecked /> Comentarios en informes</CheckRow>
            </Field>
            <Field>
              <label>Frecuencia de resúmenes</label>
              <TechSelect>
                <option>Diario</option>
                <option>Semanal</option>
                <option>Quincenal</option>
                <option>Mensual</option>
              </TechSelect>
            </Field>
            <Actions>
              <TechButtonGhost type="button">Cancelar</TechButtonGhost>
              <TechButton type="button"><FaSave /> Guardar preferencias</TechButton>
            </Actions>
          </>
        );

      case 'data':
        return (
          <>
            <Field>
              <label>Retención de datos</label>
              <TechSelect>
                <option>6 meses</option>
                <option>1 año</option>
                <option>2 años</option>
                <option>Indefinido</option>
              </TechSelect>
              <p className="help">Período por el que se conservan los datos históricos.</p>
            </Field>
            <Field>
              <label>Formato predeterminado de exportación</label>
              <TechSelect>
                <option>CSV</option>
                <option>Excel</option>
                <option>JSON</option>
                <option>PDF</option>
              </TechSelect>
            </Field>
            <Field>
              <label>Respaldo automático</label>
              <CheckRow><input type="checkbox" defaultChecked /> Habilitar respaldo automático semanal</CheckRow>
            </Field>
            <Actions>
              <TechButtonGhost type="button">Exportar todos los datos</TechButtonGhost>
              <TechButton type="button"><FaSave /> Guardar configuración</TechButton>
            </Actions>
          </>
        );

      case 'integrations':
        return (
          <>
            {apiIntegrations.map((api, i) => (
              <IntegrationRow key={i}>
                <div>
                  <h5>{api.name}</h5>
                  <p>{api.description}</p>
                  <span className="meta">Última sync · {api.lastSync}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                  <TechChip tone={statusTone(api.status)}>
                    {api.status === 'success' ? <FaCheck /> : <FaExclamationTriangle />}
                    {statusLabel(api.status)}
                  </TechChip>
                  <div style={{ display: 'flex', gap: 5 }}>
                    <TechButtonGhost type="button" style={{ padding: '0.35rem 0.7rem', fontSize: '0.75rem' }}>Configurar</TechButtonGhost>
                    <TechButton type="button" style={{ padding: '0.35rem 0.7rem', fontSize: '0.75rem' }}>Reconectar</TechButton>
                  </div>
                </div>
              </IntegrationRow>
            ))}
            <Actions>
              <TechButton type="button"><FaPlug /> Añadir integración</TechButton>
            </Actions>
          </>
        );

      case 'social':
        return <SocialPanel />;

      case 'dummy-accounts':
        return <DummyAccountsPanel />;

      case 'elections':
        return <ElectionsTab />;

      default:
        return null;
    }
  };

  const activeNav = NAV.find((n) => n.id === activeSection);

  return (
    <TechShell>
      <TechPageHeader
        title="Configuración"
        subtitle="Ajustes de campaña, perfil, seguridad e integraciones"
        dossier="CFG://SYS/01"
      />

      <Layout>
        <Sidebar>
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavBtn
                key={n.id}
                active={activeSection === n.id}
                onClick={() => setActiveSection(n.id)}
                type="button"
              >
                <Icon /> {n.label}
              </NavBtn>
            );
          })}
        </Sidebar>

        <TechSection title={activeNav?.label || 'Configuración'} icon={activeNav?.icon ? <activeNav.icon /> : null}>
          {renderContent()}
        </TechSection>
      </Layout>
    </TechShell>
  );
};

export default Configuration;
